import { useRecoilValue, useSetRecoilState } from "recoil"
import { RENDER_STREAM_ON_CANVAS } from "../../constants"
import { mainWindowUserSelector } from "../../recoil/atoms/mainWindowUser"
import { playerPropsSelector } from "../../recoil/atoms/player"
import { useBrowserType } from "../../hooks/useBrowserType"
import { isBigPlayIconState } from "../../recoil/atoms/bigPlayIcon"

interface VideoProps extends HTMLVideoElement {
  mozRequestFullScreen(): Promise<void>
  webkitRequestFullScreen(): Promise<void>
  requestFullScreen(): Promise<void>
}

export const useFullScreenMobile = () => {
  const mainWindowUser = useRecoilValue(mainWindowUserSelector)
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const setBigPlayIcon = useSetRecoilState(isBigPlayIconState)
  const { isSafariMobile } = useBrowserType()

  const isInFullScreen =
    (document.fullscreenElement && document.fullscreenElement !== null) ||
    (document.webkitFullscreenElement &&
      document.webkitFullscreenElement !== null) ||
    (document.mozFullScreenElement && document.mozFullScreenElement !== null) ||
    (document.msFullscreenElement && document.msFullscreenElement !== null)

  // Setup fullscreen exit listener for hiding video tag
  function setupFullscreenExitListener(id?: string) {

    // Get Cam video element
    const container = document.getElementById(
      id ?? `player-1337-${mainWindowUser?.uid}`
    ) as HTMLDivElement

    let videoElement
    if (id === "watchpartyvideoplayer") {
      videoElement = container
    } else {
      videoElement = container.getElementsByTagName("video")?.[0] as VideoProps
    }

    // Add fullscreen listener
    document.addEventListener("fullscreenchange", exitHandler, false)
    document.addEventListener("mozfullscreenchange", exitHandler, false)
    document.addEventListener("MSFullscreenChange", exitHandler, false)
    document.addEventListener("webkitfullscreenchange", exitHandler, false)
    videoElement?.addEventListener("webkitendfullscreen", exitHandler, false)


    function exitHandler() {
      const isInFullScreen =
        (document.fullscreenElement && document.fullscreenElement !== null) ||
        (document.webkitFullscreenElement &&
          document.webkitFullscreenElement !== null) ||
        (document.mozFullScreenElement &&
          document.mozFullScreenElement !== null) ||
        (document.msFullscreenElement && document.msFullscreenElement !== null)

      if (!isInFullScreen) {
        if (videoElement && videoElement.id !== id) videoElement.style.visibility = "hidden"

          if (isSafariMobile) {
            setPlayerProps({ paused: true })
            setBigPlayIcon(true)
          } else {
            videoElement.play()
          }
          
          // Remove event listeners after the first occurrence
          document.removeEventListener("fullscreenchange", exitHandler)
          document.removeEventListener("mozfullscreenchange", exitHandler)
          document.removeEventListener("MSFullscreenChange", exitHandler)
          document.removeEventListener("webkitfullscreenchange", exitHandler)
          videoElement?.removeEventListener(
            "webkitendfullscreen",
            exitHandler,
            false
          )
      }
    }
  }

  const handleFullScreenMobileWebCam = (id?: string) => {
    const container = document.getElementById(
      id ?? `player-1337-${mainWindowUser?.uid}`
    ) as HTMLDivElement
    // Get Cam video element
    let video
    if (id === "watchpartyvideoplayer") {
      video = container
    } else {
      video = container.getElementsByTagName("video")?.[0] as VideoProps
    }

    if (video) {
      if (!isInFullScreen) {
        // Make video tag visible - when Stream is render on canvas 
        if(RENDER_STREAM_ON_CANVAS && !id) {
          video.style.visibility = "visible"
          video.play()
          setupFullscreenExitListener()
        } else if (id) {
          setupFullscreenExitListener(id)
        }

        if (video.requestFullScreen) {
          // W3C API
          video.requestFullScreen()
        } else if (video.mozRequestFullScreen) {
          // Mozilla current API
          video.mozRequestFullScreen()
        } else if (video.webkitRequestFullScreen) {
          // Webkit current API
          video.webkitRequestFullScreen()
        } else if (video.webkitEnterFullScreen) {
          // This is the IOS Mobile edge case
          video.webkitEnterFullScreen()
        }
      } else {
        // Make video tag hidden as a stream is render on canvas 
        if(RENDER_STREAM_ON_CANVAS) {
          video.style.visibility = "hidden"
        }

        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      }
    }
  }

  return {
    isInFullScreen,
    handleFullScreenMobileWebCam
  }
}
